import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Breadcrumb,
  Button,
  Table,
  Input,
  Space,
  Typography,
  Tag,
  Divider,
  Popover,
  Spin,
  Tooltip,
  Segmented,
  Select,
  Switch,
  Card,
} from "antd";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import {
  SearchOutlined,
  EditOutlined,
  CopyOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { formatDistance } from "date-fns";
import { it } from "date-fns/locale";
import FeatureEditModal from "./FeatureEditModal";
import ParseLogo from "../../images/logos/parse.png";
import { isRecentlyChanged, RECENT_DAYS } from "./recentUtils";

const { Title, Text } = Typography;

const PREVIEW_ACTIONS_COUNT = 5;

const ACTION_COLORS = {
  create: "green",
  delete: "red",
  update: "orange",
  modify: "orange",
  show: "cyan",
  clone: "blue",
  invite: "magenta",
  associate: "gold",
  dissociate: "gold",
  share: "lime",
  send: "purple",
  connect: "purple",
  statistics: "geekblue",
  assign: "geekblue",
  default: "default",
};

const PLAN_COLORS = {
  basic: "default",
  partner: "geekblue",
  plus: "cyan",
  pro: "blue",
  enterprise: "purple",
  obituary: "magenta",
  legacy_upsell: "volcano",
};

const PLAN_ORDER = [
  "basic",
  "partner",
  "plus",
  "pro",
  "enterprise",
  "obituary",
  "legacy_upsell",
];

const WARN_COLOR = "#d46b08";

const planRank = (p) => {
  const i = PLAN_ORDER.indexOf(p);
  return i === -1 ? 99 : i;
};
const byPlanOrder = (a, b) => planRank(a) - planRank(b) || a.localeCompare(b);
const getActionColor = (action) => {
  const actionLower = (action || "").toLowerCase();
  return (
    Object.entries(ACTION_COLORS).find(([key]) =>
      actionLower.includes(key)
    )?.[1] || ACTION_COLORS.default
  );
};
const planColor = (plan) => PLAN_COLORS[plan] || "default";

const query = `
  query getFeatures($where: FeaturesWhereInput!, $first: Int!) {
    findFeatures(where: $where, first: $first) {
      edges {
        node {
          objectId
          description
          ofc_managed
          label
          name
          default
          products {
            ... on Element {
              value
            }
          }
          plans {
            ... on Element {
              value
            }
          }
          guide_article_id
          module_pointer {
            objectId
            name
          }
          createdAt
          updatedAt
        }
      }
    }
  }
`
  .replace(/\r?\n|\r/g, "")
  .replace(/\t/g, " ");

const Features = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [planFilter, setPlanFilter] = useState([]);
  const [moduleFilter, setModuleFilter] = useState([]);
  const [ofcOnly, setOfcOnly] = useState(false);
  const [defaultOnly, setDefaultOnly] = useState(false);
  const [recentOnly, setRecentOnly] = useState(false);
  const [qualityFilter, setQualityFilter] = useState(""); // "" | "noplan" | "nomod"
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const { getTokenSilently } = useAuth0();
  const graphqlUrl = `${config.baseUrl[env]}/graphql`;
  const parseDashboardUrl = config.parseDashboard[env];
  const [editingFeature, setEditingFeature] = useState(null);
  const [modalDraftSeed, setModalDraftSeed] = useState({});
  const [modalSessionKey, setModalSessionKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const token = await getTokenSilently();
      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: {
          query,
          variables: {
            where: {},
            first: 10000,
          },
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const items = response.data || [];
      const featuresData = items.sort((a, b) => a.name.localeCompare(b.name));

      // Group features by the base name (before the dot)
      const groupedFeatures = featuresData.reduce((acc, feature) => {
        const baseName = feature.name.split(".")[0];
        if (!acc[baseName]) {
          acc[baseName] = {
            key: baseName,
            name: baseName,
            actions: [],
          };
        }
        const action = feature.name.split(".").slice(1).join(".");
        acc[baseName].actions.push({
          ...feature,
          key: feature.objectId,
          action,
        });
        return acc;
      }, {});

      const processedFeatures = Object.values(groupedFeatures);
      setFeatures(processedFeatures);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching features:", error);
      setLoading(false);
    }
  }, [getTokenSilently, graphqlUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Aggregate stats (action level) ----
  const stats = useMemo(() => {
    let total = 0,
      eterno = 0,
      giotto = 0,
      ofc = 0,
      def = 0,
      noplan = 0,
      nomod = 0;
    features.forEach((g) =>
      g.actions.forEach((a) => {
        total++;
        if (a.products?.some((p) => p.value === "eterno")) eterno++;
        if (a.products?.some((p) => p.value === "giotto")) giotto++;
        if (a.ofc_managed) ofc++;
        if (a.default) def++;
        if (!a.plans?.length) noplan++;
        if (!a.module_pointer) nomod++;
      })
    );
    return { total, groups: features.length, eterno, giotto, ofc, def, noplan, nomod };
  }, [features]);

  const planOptions = useMemo(() => {
    const set = new Set();
    features.forEach((g) =>
      g.actions.forEach((a) => (a.plans || []).forEach((p) => set.add(p.value)))
    );
    return [...set]
      .sort(byPlanOrder)
      .map((value) => ({ value, label: value }));
  }, [features]);

  const moduleOptions = useMemo(() => {
    const map = new Map();
    features.forEach((g) =>
      g.actions.forEach((a) => {
        if (a.module_pointer)
          map.set(a.module_pointer.objectId, a.module_pointer.name);
      })
    );
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [features]);

  // ---- Filtering ----
  const normalizedSearch = globalSearchText.trim().toLowerCase();
  const hasActiveFilter =
    !!normalizedSearch ||
    !!productFilter ||
    planFilter.length > 0 ||
    moduleFilter.length > 0 ||
    ofcOnly ||
    defaultOnly ||
    recentOnly ||
    !!qualityFilter;

  const rollup = (acts) => {
    const prods = new Set();
    const plans = new Set();
    const modMap = new Map();
    let incomplete = 0;
    acts.forEach((a) => {
      (a.products || []).forEach((p) => prods.add(p.value));
      (a.plans || []).forEach((p) => plans.add(p.value));
      if (a.module_pointer) modMap.set(a.module_pointer.objectId, a.module_pointer.name);
      if (!a.module_pointer) incomplete++;
    });
    return {
      products: [...prods],
      plans: [...plans].sort(byPlanOrder),
      modules: [...modMap.entries()]
        .map(([objectId, name]) => ({ objectId, name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      incomplete,
    };
  };

  const filteredFeatures = useMemo(() => {
    const q = normalizedSearch;
    const onlyTextSearch =
      !!q &&
      !productFilter &&
      planFilter.length === 0 &&
      moduleFilter.length === 0 &&
      !ofcOnly &&
      !defaultOnly &&
      !recentOnly &&
      !qualityFilter;

    const actionMatches = (a) => {
      if (q) {
        const inText =
          (a.name || "").toLowerCase().includes(q) ||
          (a.action || "").toLowerCase().includes(q) ||
          (a.label || "").toLowerCase().includes(q) ||
          (a.description || "").toLowerCase().includes(q);
        if (!inText) return false;
      }
      if (productFilter && !a.products?.some((p) => p.value === productFilter))
        return false;
      if (planFilter.length && !a.plans?.some((p) => planFilter.includes(p.value)))
        return false;
      if (
        moduleFilter.length &&
        !(a.module_pointer && moduleFilter.includes(a.module_pointer.objectId))
      )
        return false;
      if (ofcOnly && !a.ofc_managed) return false;
      if (defaultOnly && !a.default) return false;
      if (recentOnly && !isRecentlyChanged(a)) return false;
      if (qualityFilter === "noplan" && a.plans?.length) return false;
      if (qualityFilter === "nomod" && a.module_pointer) return false;
      return true;
    };

    const decorate = (group, acts) => {
      const view = rollup(acts);
      const previews = [...new Set(acts.map((a) => a.action))];
      return {
        ...group,
        actions: acts,
        actionCount: acts.length,
        actionPreviews: previews.slice(0, PREVIEW_ACTIONS_COUNT),
        previewExtra: Math.max(0, previews.length - PREVIEW_ACTIONS_COUNT),
        view,
      };
    };

    const out = [];
    features.forEach((group) => {
      const acts = group.actions.filter(actionMatches);
      if (acts.length) {
        out.push(decorate(group, acts));
      } else if (onlyTextSearch && group.name.toLowerCase().includes(q)) {
        out.push(decorate(group, group.actions));
      }
    });
    return out;
  }, [
    features,
    normalizedSearch,
    productFilter,
    planFilter,
    moduleFilter,
    ofcOnly,
    defaultOnly,
    recentOnly,
    qualityFilter,
  ]);

  const shownActions = useMemo(
    () => filteredFeatures.reduce((acc, g) => acc + g.actionCount, 0),
    [filteredFeatures]
  );

  const effectiveExpandedRowKeys = hasActiveFilter
    ? filteredFeatures.filter((f) => f.actionCount > 1).map((f) => f.name)
    : expandedRowKeys;

  const toggleExpand = (name) =>
    setExpandedRowKeys((keys) =>
      keys.includes(name) ? keys.filter((k) => k !== name) : [...keys, name]
    );

  const resetFilters = () => {
    setGlobalSearchText("");
    setProductFilter("");
    setPlanFilter([]);
    setModuleFilter([]);
    setOfcOnly(false);
    setDefaultOnly(false);
    setRecentOnly(false);
    setQualityFilter("");
  };

  // ---- Highlight helper ----
  const renderHighlight = (text) => {
    const value = text ? text.toString() : "";
    if (!normalizedSearch) return value || "-";
    return (
      <Highlighter
        highlightStyle={{ backgroundColor: "#ffe58f", padding: 0 }}
        searchWords={[globalSearchText]}
        autoEscape
        textToHighlight={value}
      />
    );
  };

  const renderPlans = (plans, max) => {
    if (!plans || plans.length === 0)
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>nessun piano</Text>
      );
    const show = max ? plans.slice(0, max) : plans;
    return (
      <Space size={[4, 4]} wrap>
        {show.map((plan) => (
          <Tag key={plan} color={planColor(plan)} style={{ marginInlineEnd: 0 }}>
            {plan}
          </Tag>
        ))}
        {max && plans.length > max && (
          <Tag style={{ marginInlineEnd: 0 }}>+{plans.length - max}</Tag>
        )}
      </Space>
    );
  };

  const renderProducts = (products) => {
    if (!products || products.length === 0) return "-";
    return (
      <Space size={[4, 4]} wrap>
        {products.map((value) => (
          <Tag
            key={value}
            color={value === "giotto" ? "blue" : "purple"}
            style={{ marginInlineEnd: 0 }}
          >
            {value}
          </Tag>
        ))}
      </Space>
    );
  };

  // ---- Group (top-level) columns ----
  const columns = [
    {
      title: "Feature",
      dataIndex: "name",
      key: "name",
      width: "23%",
      render: (text, record) => (
        <Space>
          <span
            style={{
              background: record.actionCount === 1 ? "#fafafa" : "#e6f4ff",
              color: record.actionCount === 1 ? "rgba(0,0,0,.45)" : "#0958d9",
              border:
                record.actionCount === 1
                  ? "1px solid #d9d9d9"
                  : "1px solid #91caff",
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              padding: "0 7px",
              lineHeight: "17px",
              minWidth: 18,
              textAlign: "center",
            }}
          >
            {record.actionCount}
          </span>
          <span style={{ fontFamily: "monospace", fontWeight: 500 }}>
            {renderHighlight(text)}
          </span>
          {record.view.incomplete > 0 && (
            <Tooltip
              title={`${record.view.incomplete} azioni senza modulo`}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: WARN_COLOR,
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Azioni",
      dataIndex: "actionPreviews",
      key: "actionPreviews",
      width: "27%",
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          {record.actionPreviews.map((action, index) => (
            <Tag
              key={index}
              color={getActionColor(action)}
              style={{ marginInlineEnd: 0 }}
            >
              {action}
            </Tag>
          ))}
          {record.previewExtra > 0 && (
            <Tag style={{ marginInlineEnd: 0 }}>+{record.previewExtra} more</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Prodotti",
      key: "products",
      width: "12%",
      render: (_, record) => renderProducts(record.view.products),
    },
    {
      title: "Piani",
      key: "plans",
      width: "23%",
      render: (_, record) => renderPlans(record.view.plans, PREVIEW_ACTIONS_COUNT),
    },
    {
      title: "Moduli",
      key: "modules",
      width: "15%",
      render: (_, record) => {
        const mods = record.view.modules;
        if (mods.length === 0) return "-";
        if (mods.length === 1) return <Tag color="geekblue">{mods[0].name}</Tag>;
        return (
          <Popover
            title="Moduli associati"
            trigger="hover"
            content={
              <div style={{ maxWidth: 360 }}>
                {mods.map((m) => (
                  <Tag key={m.objectId} color="geekblue" style={{ margin: 2 }}>
                    {m.name}
                  </Tag>
                ))}
              </div>
            }
          >
            <Tag color="geekblue" style={{ cursor: "pointer" }}>
              {mods.length} moduli
            </Tag>
          </Popover>
        );
      },
    },
  ];

  // ---- Expanded detail columns (compact) ----
  const expandedColumns = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "12%",
      render: (action) => <Tag color={getActionColor(action)}>{action}</Tag>,
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      width: "20%",
      render: (label) => renderHighlight(label),
    },
    {
      title: "Piani",
      dataIndex: "plans",
      key: "plans",
      width: "18%",
      render: (plans) => renderPlans((plans || []).map((p) => p.value).sort(byPlanOrder)),
    },
    {
      title: "Prodotti",
      dataIndex: "products",
      key: "products",
      width: "10%",
      render: (products) => renderProducts((products || []).map((p) => p.value)),
    },
    {
      title: "Modulo",
      key: "module",
      width: "14%",
      render: (_, record) =>
        record.module_pointer ? (
          <Tag color="geekblue">{record.module_pointer.name}</Tag>
        ) : (
          <Text style={{ color: WARN_COLOR, fontSize: 12 }}>⚠ —</Text>
        ),
    },
    {
      title: "OFC",
      dataIndex: "ofc_managed",
      key: "ofc_managed",
      width: "5%",
      align: "center",
      render: (managed) => (
        <Tooltip title={managed ? "Gestita da OFC" : "Non gestita da OFC"}>
          <span style={{ color: managed ? "#389e0d" : "#bfbfbf", fontSize: 16 }}>
            {managed ? "●" : "○"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Sempre ON",
      dataIndex: "default",
      key: "default",
      width: "6%",
      align: "center",
      render: (defaultValue) =>
        defaultValue ? (
          <Tag color="green" style={{ marginInlineEnd: 0 }}>
            ON
          </Tag>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            off
          </Text>
        ),
    },
    {
      title: "Guida",
      dataIndex: "guide_article_id",
      key: "guide_article_id",
      width: "6%",
      render: (id) =>
        id ? (
          <a
            href={`https://app.intercom.com/a/apps/_/articles/articles/${id}/show`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            guida
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Aggiornato",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "9%",
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      render: (date) =>
        date ? (
          <Tooltip title={new Date(date).toLocaleString("it-IT")}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDistance(new Date(date), new Date(), { locale: it })}
            </Text>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          />
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleClone(record);
            }}
          />
          <a
            href={`${parseDashboardUrl}/browser/features?filters=[{"field"%3A"objectId","constraint"%3A"eq","compareTo"%3A"${record.objectId}"}]`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ display: "inline-flex", alignItems: "center", padding: "0 6px" }}
          >
            <img
              src={ParseLogo}
              alt="Parse Dashboard"
              style={{ width: 18, height: 18, objectFit: "contain" }}
            />
          </a>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      columns={expandedColumns}
      dataSource={record.actions}
      pagination={false}
      size="small"
      rowKey="objectId"
      onRow={(action) => ({
        style: { cursor: "pointer" },
        onClick: () => handleEdit(action),
      })}
    />
  );

  const handleEdit = (record) => {
    setEditingFeature(record);
    setModalDraftSeed(record);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleNew = () => {
    setEditingFeature(null);
    setModalDraftSeed({});
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setEditingFeature(null);
    setIsModalVisible(false);
  };

  const handleSuccess = () => {
    setEditingFeature(null);
    setIsModalVisible(false);
    fetchData();
  };

  const handleClone = (record) => {
    const clonedFeature = {
      ...record,
      name: `${record.name}_copy`,
      objectId: null,
    };
    setEditingFeature(clonedFeature);
    setModalDraftSeed(clonedFeature);
    setModalSessionKey((current) => current + 1);
    setIsModalVisible(true);
  };

  // ---- KPI cards ----
  const kpis = [
    { key: "", label: "Azioni totali", value: stats.total, sub: `${stats.groups} gruppi` },
    { key: "prod:eterno", label: "Eterno", value: stats.eterno },
    { key: "prod:giotto", label: "Giotto", value: stats.giotto },
    { key: "ofc", label: "OFC managed", value: stats.ofc },
    { key: "noplan", label: "Senza piano", value: stats.noplan },
    { key: "nomod", label: "Senza modulo", value: stats.nomod, warn: true },
  ];

  const isKpiActive = (key) =>
    (key === "ofc" && ofcOnly) ||
    (key === "prod:eterno" && productFilter === "eterno") ||
    (key === "prod:giotto" && productFilter === "giotto") ||
    (key === "noplan" && qualityFilter === "noplan") ||
    (key === "nomod" && qualityFilter === "nomod");

  const onKpiClick = (key) => {
    if (key === "ofc") setOfcOnly((v) => !v);
    else if (key === "prod:eterno")
      setProductFilter((v) => (v === "eterno" ? "" : "eterno"));
    else if (key === "prod:giotto")
      setProductFilter((v) => (v === "giotto" ? "" : "giotto"));
    else if (key === "noplan")
      setQualityFilter((v) => (v === "noplan" ? "" : "noplan"));
    else if (key === "nomod")
      setQualityFilter((v) => (v === "nomod" ? "" : "nomod"));
  };

  return (
    <div style={{ margin: "16px 16px" }}>
      <Spin spinning={loading}>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[{ title: "Configurazioni" }, { title: "Features" }]}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Features
            </Title>
            <Text type="secondary">
              Catalogo dei permessi applicativi — mappatura ai moduli, prodotti
              e guide Intercom.
            </Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNew}>
            Nuova Feature
          </Button>
        </div>

        {/* KPI strip */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 12,
            marginBottom: 16,
          }}
        >
          {kpis.map((kpi) => {
            const clickable = kpi.key !== "";
            const active = isKpiActive(kpi.key);
            return (
              <Card
                key={kpi.label}
                size="small"
                hoverable={clickable}
                onClick={clickable ? () => onKpiClick(kpi.key) : undefined}
                style={{
                  cursor: clickable ? "pointer" : "default",
                  borderColor: active ? "#1677ff" : undefined,
                  background: active ? "#f0f7ff" : undefined,
                }}
                styles={{ body: { padding: "12px 14px" } }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: kpi.warn ? WARN_COLOR : "rgba(0,0,0,.45)",
                    marginBottom: 4,
                  }}
                >
                  {kpi.warn && (
                    <span
                      style={{
                        display: "inline-block",
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: WARN_COLOR,
                        marginRight: 6,
                      }}
                    />
                  )}
                  {kpi.label}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    lineHeight: 1.1,
                    color: kpi.warn ? WARN_COLOR : undefined,
                  }}
                >
                  {kpi.value}
                  {kpi.sub && (
                    <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
                      {" "}
                      · {kpi.sub}
                    </Text>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
            marginBottom: 12,
          }}
        >
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Cerca per nome, action, label o descrizione..."
            value={globalSearchText}
            onChange={(e) => setGlobalSearchText(e.target.value)}
            style={{ width: 340 }}
          />
          <Segmented
            value={productFilter}
            onChange={setProductFilter}
            options={[
              { label: "Tutti i prodotti", value: "" },
              { label: "eterno", value: "eterno" },
              { label: "giotto", value: "giotto" },
            ]}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Piani"
            value={planFilter}
            onChange={setPlanFilter}
            options={planOptions}
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            style={{ width: 200 }}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Modulo"
            value={moduleFilter}
            onChange={setModuleFilter}
            options={moduleOptions}
            maxTagCount={1}
            maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            style={{ width: 220 }}
          />
          <Tooltip title="Solo feature gestite da OFC">
            <Space size={6}>
              <Switch size="small" checked={ofcOnly} onChange={setOfcOnly} />
              <Text type="secondary">OFC managed</Text>
            </Space>
          </Tooltip>
          <Tooltip title="Solo feature sempre abilitate">
            <Space size={6}>
              <Switch size="small" checked={defaultOnly} onChange={setDefaultOnly} />
              <Text type="secondary">Sempre abilitate</Text>
            </Space>
          </Tooltip>
          <Tooltip title={`Create o aggiornate negli ultimi ${RECENT_DAYS} giorni`}>
            <Space size={6}>
              <Switch size="small" checked={recentOnly} onChange={setRecentOnly} />
              <Text type="secondary">Recenti</Text>
            </Space>
          </Tooltip>
          <div style={{ flex: 1 }} />
          <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
            {filteredFeatures.length} gruppi · {shownActions} azioni
          </Text>
          {hasActiveFilter && (
            <Button type="link" onClick={resetFilters} style={{ padding: 0 }}>
              Azzera filtri
            </Button>
          )}
        </div>

        <Table
          dataSource={filteredFeatures}
          columns={columns}
          loading={loading}
          size="small"
          pagination={false}
          rowKey="name"
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => record.actionCount > 1,
            expandedRowKeys: effectiveExpandedRowKeys,
            onExpand: (expanded, record) => {
              if (!hasActiveFilter) toggleExpand(record.name);
            },
            expandIcon: ({ record, onExpand }) =>
              record.actionCount > 1 ? (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e);
                  }}
                  style={{
                    cursor: "pointer",
                    display: "inline-block",
                    transition: "transform .15s",
                    color: effectiveExpandedRowKeys.includes(record.name)
                      ? "#1677ff"
                      : "rgba(0,0,0,.45)",
                    transform: effectiveExpandedRowKeys.includes(record.name)
                      ? "rotate(90deg)"
                      : "none",
                  }}
                >
                  ▶
                </span>
              ) : (
                <span style={{ color: "#d9d9d9" }}>●</span>
              ),
          }}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => {
              if (record.actionCount > 1) {
                if (!hasActiveFilter) toggleExpand(record.name);
              } else {
                handleEdit(record.actions[0]);
              }
            },
          })}
        />

        <div style={{ marginTop: 12, color: "rgba(0,0,0,.45)", fontSize: 12 }}>
          <Space split={<Divider type="vertical" />} wrap>
            <span>
              <span
                style={{
                  display: "inline-block",
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: WARN_COLOR,
                  marginRight: 6,
                }}
              />
              pallino arancione = azioni senza modulo
            </span>
            <span>gruppi con badge grigio = singola azione (click diretto)</span>
          </Space>
        </div>

        <FeatureEditModal
          visible={isModalVisible}
          feature={editingFeature}
          draftSeed={modalDraftSeed}
          sessionKey={modalSessionKey}
          onCancel={handleCancel}
          onSuccess={handleSuccess}
        />
      </Spin>
    </div>
  );
};

export default Features;
